import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { InboxService } from '../../../../../../../../services/inbox/inbox.service';
import { UnlinkedEntryPassCustomerInboxItem } from '../../../../../../../../models/inbox';
import { dateStringFromISO8601, getISO8601DateStringFromMoment, propExists } from '../../../../../../../../utils';
import { AccountService } from '../../../../../../../../services/account/account.service';
import { CustomerService } from '../../../../../../../../services/customer/customer.service';
import { CustomerSearchResult } from '../../../../../../../../models/customer';
import { Subject } from 'rxjs';
import { ListResponseCountOptional } from '../../../../../../../../models/list-response';
import { debounceTime } from 'rxjs/operators';
import * as moment from 'moment';
import {
  OrganizationConfigService
} from '../../../../../../../../services/organization-config/organization-config.service';

enum Action {
  link,
  createNew
}

@Component({
  selector: 'app-review-unlinked-entry-pass-customer-modal',
  templateUrl: './review-unlinked-entry-pass-customer-modal.component.html',
  styleUrls: ['./review-unlinked-entry-pass-customer-modal.component.css']
})
export class ReviewUnlinkedEntryPassCustomerModalComponent implements OnInit {

  @Input() unlinkedCustomerInboxItem: UnlinkedEntryPassCustomerInboxItem;
  @Input() savedCallback: () => undefined;

  public selectedAction: Action = null;
  public selectedCustomer: CustomerSearchResult = null;

  public loadingSubmit = false;
  public loadingCustomers = false;

  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: ListResponseCountOptional<CustomerSearchResult>;

  public errorMessage = '';

  public serverError: number = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly Action = Action;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    private customerService: CustomerService,
    private inboxService: InboxService,
    private orgConfigService: OrganizationConfigService,
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    this.loadingCustomers = true;
    this.selectedCustomer = null;

    const orgConfig = this.orgConfigService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      this.customerList.results &&
      (this.customerList.results.length > 0)
    );
  }

  public selectCustomer(customer: CustomerSearchResult) {
    this.selectedCustomer = customer;
    this.search = (
      customer.first_name + ' ' + customer.last_name
    );
  }

  /**
   * Whether a valid input has been selected. Used to determine whether the
   * save button is clickable.
   */
  public validInputSelected(): boolean {
    switch (this.selectedAction) {
      case Action.link:
        return this.selectedCustomer !== null;
      case Action.createNew:
        return true;
      default:
        return false;
    }
  }

  public handleSave() {
    this.loadingSubmit = true;

    this.errorMessage = '';

    if (!this.validInputSelected()) {
      this.errorMessage = 'Please choose a valid option.';
      this.loadingSubmit = false;
      return;
    }

    if (this.selectedAction === Action.link) {
      this.inboxService
        .linkUnlinkedEntryPassCustomer(
          this.unlinkedCustomerInboxItem.id,
          this.selectedCustomer.id
        )
        .then(() => {
          this.inboxService.loadInboxCounts(true).then();
          this.savedCallback();
          this.activeModal.close();
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errorMessage = reason.error;
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to link this entry pass.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    } else if (this.selectedAction === Action.createNew) {
      this.inboxService
        .createNewCustomerForUnlinkedEntryPassCustomer(
          this.unlinkedCustomerInboxItem.id
        )
        .then(() => {
          this.inboxService.loadInboxCounts(true).then();
          this.savedCallback();
          this.activeModal.close();
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.errorMessage = reason.error;
          } else if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to link this entry pass.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    }
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  private loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

}
